/*
 *  -----------------------------------------------------------------------
 *    <copyright file="edit-system-user.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FormControl from '@mui/material/FormControl';
import { Checkbox } from '@progress/kendo-react-inputs';
import { useBoolean, useTranslation } from 'hooks';
import { SystemUserFields } from 'queries/generated/fragments.generated';
import { SAVE_SYSTEM_USER, GET_SYSTEM_USERS } from 'queries/uam';
import { OrganisationMemberStatus } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import { checkValidity } from 'utils/shared';
import { validateFields } from 'utils/validation';
import { getAdminLevelName } from 'views/components/admin/users/system-users-service';
import { SaveButton } from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogTitle, DialogContent, DialogActions } from 'views/components/core/dialog';
import ModalUpdatedInfo from 'views/components/core/modal-updated-info';
import CustomPhoneInput from 'views/components/core/phone-input';
import SelectWithCategories from 'views/components/core/select-with-categories';
import TextField from 'views/components/core/text-field';

interface IExtentedSystemUser extends SystemUserFields {
    _status?: string;
}

const EditSystemUser = ({
    onClose,
    open,
    user,
    systemUsers,
    onExistEmail,
    loggedUser,
}: {
    onClose: Function;
    open: boolean;
    user: IExtentedSystemUser;
    systemUsers: IExtentedSystemUser[];
    onExistEmail: Function;
    loggedUser: any;
}) => {
    const { t } = useTranslation();
    const initialState = {
        systemAdminLevel: 0,
        active: false,
        email: '',
        phone: '',
        firstName: '',
        lastName: '',
    };

    const disabledSave = useBoolean(true);
    const saving = useBoolean(false);
    const isResent = useBoolean(true);
    const [saveMember] = useMutation(SAVE_SYSTEM_USER);
    const [errors, setErrors] = useState({} as any);
    const [state, setState] = useState({ ...initialState });
    const [phoneError, setPhoneError] = useState(null);

    const generateArray = () => {
        const result =
            Array.from(Array(4).keys()).map((item: any) => ({
                id: item + 1,
                text: getAdminLevelName(item + 1),
            })) ?? [];

        result.unshift({
            id: 0,
            text: t('ra:selectValue'),
        });

        return result;
    };

    const systemLevels = useMemo(() => generateArray(), []);

    useEffect(() => {
        if (open && user)
            setState({
                systemAdminLevel: user.systemAdminLevel || state.systemAdminLevel,
                active: user.isEnabled || state.active,
                email: user.email || state.email,
                phone: user.phone || state.phone,
                firstName: user.firstName || state.firstName,
                lastName: user.lastName || state.lastName,
            });
        if (open && !user) setState({ ...initialState });
    }, [user, open]);

    const close = (action?: string | boolean) => {
        if (action !== 'error' && action !== 'resent') {
            setState({ ...initialState });
            setErrors({});
            isResent.setTrue();
            disabledSave.setTrue();
        }
        onClose(action);
    };

    const onToggle = (name: string, value: boolean) => {
        disabledSave.setFalse();
        setState({
            ...state,
            [name]: value,
        });
    };

    const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
        const error = validateFields(value, field, length, required);
        setErrors({
            ...errors,
            ...error,
        });
    };

    const onChange = (name: string) => (event: any) => {
        disabledSave.setFalse();
        let length = 50;
        if (name === 'email') length = 320;

        setState({
            ...state,
            [name]: event.target.value,
        });
        const isValidEmail =
            name === 'email' && event.target.value.length ? checkValidity(event.target.value, { isEmail: true }) : true;
        if (!isValidEmail) {
            setErrors({
                ...errors,
                email: t('invalidEmail'),
            });
        } else {
            validate(event.target.value, name, length, false);
        }
    };

    const validateOnUpdate = () => {
        const errorPhone = {
            phone: phoneError,
        };
        const errorLastName = validateFields(state.lastName, 'lastName', 60, true);
        const errorFirstName = validateFields(state.firstName, 'firstName', 60, true);
        const isValidEmail = state.email.length ? checkValidity(state.email, { isEmail: true }) : true;
        const errorEmail = isValidEmail
            ? validateFields(state.email, 'email', 320, true)
            : {
                  email: t('invalidEmail'),
              };

        const errorLevel = {
            systemAdminLevel: state.systemAdminLevel === 0 ? t('ra:requiredFieldError') : null,
        };

        setErrors({
            ...errors,
            ...errorLastName,
            ...errorFirstName,
            ...errorPhone,
            ...errorEmail,
            ...errorLevel,
        });

        if (
            errorLastName?.lastName ||
            errorFirstName?.firstName ||
            errorEmail?.email ||
            errorLevel?.systemAdminLevel ||
            errorPhone?.phone
        )
            return false;
        return true;
    };

    const submit = () => {
        const userEmail = user?.email?.trim().toLowerCase() ?? '';
        const isExistEmail = systemUsers.findIndex(
            (item: any) => item.email?.trim().toLowerCase() === state.email?.trim().toLowerCase(),
        );

        if (isExistEmail >= 0 && userEmail !== state.email) {
            onExistEmail();
            disabledSave.setTrue();
        } else {
            saving.setTrue();
            saveMember({
                variables: {
                    systemAdminLevel: state.systemAdminLevel,
                    email: state.email,
                    active: state.active,
                    firstName: state.firstName,
                    lastName: state.lastName,
                    phone: state.phone,
                    id: user?.id ?? Constants.ObjectId.Empty,
                },
                refetchQueries: [
                    {
                        query: GET_SYSTEM_USERS,
                    },
                ],
            })
                .then(() => {
                    close(user ? 'edit' : 'success');
                    saving.setFalse();
                    let isNotChanged = true;

                    if (user)
                        isNotChanged =
                            user.email === state.email &&
                            user.isEnabled === state.active &&
                            user.systemAdminLevel === state.systemAdminLevel &&
                            user.phone === state.phone;
                    if (isNotChanged && user?.id === loggedUser?.id) {
                        loggedUser.setUser({
                            ...loggedUser,
                            firstName: state.firstName,
                            lastName: state.lastName,
                        });
                    }
                })
                .catch((error: any) => {
                    console.log('creating/editing TRI user:', error);
                    close('error');
                    saving.setFalse();
                });
        }
    };

    const getStatus = (status: string) =>
        status === OrganisationMemberStatus.VERIFYING
            ? 'Verification Required'.toUpperCase()
            : status && status.toUpperCase();

    const setIsValidNumber = (value: string | null) => {
        setPhoneError(value);
    };

    return (
        <CustomDialog
            onClose={() => close()}
            aria-labelledby="add-user-title"
            fullWidth
            scroll="paper"
            maxWidth="md"
            open={open}
        >
            <DialogTitle id="add-user-title">
                {user ? (
                    <span className="edit-user-wrapper">
                        <p className="title">{`${t('uam:updateUser')}: ${user.recordId}`}</p>
                        {user.status ? (
                            <span className="user-status">{getStatus(user._status ?? user.status)}</span>
                        ) : null}
                    </span>
                ) : (
                    t('uam:addUser')
                )}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => close(false)}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="add-risk-dialog-content edit-risk">
                <div className="checkboxes-wrapper">
                    <div className="left-part">
                        <Checkbox
                            rounded="none"
                            id="user-is-active"
                            className="cell-checkbox no-border"
                            checked={state.active}
                            onChange={() => {
                                onToggle('active', !state.active);
                            }}
                            label={t('active')}
                            labelClassName="mr-20"
                            data-testid="user-is-active"
                            size="medium"
                        />
                    </div>
                </div>
                <div className="type-edc-wrapper mt">
                    <FormControl variant="outlined" className="form-control-type">
                        <SelectWithCategories
                            showWarning={false}
                            customClassName="select-outlined-role"
                            fieldSelect="systemAdminLevel"
                            onChange={onChange}
                            label={`${t('level')}*`}
                            filterable={false}
                            labelField="type"
                            error={errors.systemAdminLevel}
                            dataArray={systemLevels}
                            searchable={false}
                            customId="select-outlined-level"
                            currentState={state.systemAdminLevel}
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper no-mb">
                    <FormControl variant="outlined" className="form-control-type mr">
                        <TextField
                            id="edit-user-email"
                            autoFocus
                            label={`${t('email')}*`}
                            error={!!errors.email}
                            fullWidth
                            value={state.email}
                            onChange={onChange('email')}
                            size="small"
                            placeholder={`${t('uam:enterEmailAddress')}*`}
                            helperText={
                                <CharactersRemaining
                                    id="edit-user-email"
                                    length={state.email.length}
                                    max={320}
                                    error={errors.email}
                                />
                            }
                            variant="outlined"
                            color="secondary"
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type">
                        <CustomPhoneInput
                            customId="add-user-phone"
                            phone={state.phone}
                            label={`${t('uam:phone')}*`}
                            error={errors.phone}
                            required
                            isValid={setIsValidNumber}
                            onChange={(event: string | null | undefined) => {
                                const newEvent = { target: { value: event } };
                                onChange('phone')(newEvent);
                            }}
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper no-mb">
                    <FormControl variant="outlined" className="form-control-type mr">
                        <TextField
                            id="edit-user-first-name"
                            autoFocus
                            label={`${t('firstName')}*`}
                            error={!!errors.firstName}
                            fullWidth
                            value={state.firstName}
                            onChange={onChange('firstName')}
                            size="small"
                            placeholder={`${t('uam:enterFirstName')}*`}
                            helperText={
                                <CharactersRemaining
                                    id="edit-user-first-name"
                                    length={state.firstName.length}
                                    max={50}
                                    error={errors.firstName}
                                />
                            }
                            variant="outlined"
                            color="secondary"
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type">
                        <TextField
                            id="edit-user-last-name"
                            autoFocus
                            label={`${t('lastName')}*`}
                            error={!!errors.lastName}
                            fullWidth
                            value={state.lastName}
                            onChange={onChange('lastName')}
                            size="small"
                            placeholder={`${t('uam:enterLastName')}*`}
                            helperText={
                                <CharactersRemaining
                                    id="edit-user-last-name"
                                    length={state.lastName.length}
                                    max={50}
                                    error={errors.lastName}
                                />
                            }
                            variant="outlined"
                            color="secondary"
                            className="mb"
                        />
                    </FormControl>
                </div>
                <DialogActions className={`actions-info-wrapper-edit-cv ${!user ? 'flex-end' : ''}`}>
                    <ModalUpdatedInfo item={user} />
                    <div>
                        <SaveButton
                            id="edit-user-save"
                            disabled={
                                Object.keys(errors).filter((item) => errors[item]).length > 0 ||
                                disabledSave.value ||
                                saving.value
                            }
                            text={!user ? 'common:add' : 'common:update'}
                            onClick={() => {
                                if (!validateOnUpdate()) return;

                                submit();
                            }}
                        />
                    </div>
                </DialogActions>
            </DialogContent>
        </CustomDialog>
    );
};

export default EditSystemUser;
